import { OKENDO_STORE_URL } from '~/lib/constants';
import { generateStarRating } from '~/lib/helpers';
import { defineElement } from '~/utils/gw-utils';

const attributes = {
  overviewStars: 'data-reviews-overview-stars',
  overviewCount: 'data-reviews-overview-count',
  reviewBasedOn: 'data-reviews-based-on',
  breakdownItem: 'data-breakdown-item',
  loader: 'data-reviews-widget-loader',
};

export class ReviewsOverview extends HTMLElement {
  constructor() {
    super();
    this.reviewsWidgetModal = this.closest('.reviews-widget-modal');
    this.reviewsWidgetLoader = this.reviewsWidgetModal?.querySelector(`[${attributes.loader}]`);
    this.dataLoaded = false;
  }

  connectedCallback() {
    this.reviewsWidgetModal.addEventListener('modal-open', async () => {
      if (this.dataLoaded) return;

      const reviewsOverviewUrl = OKENDO_STORE_URL + '/review_aggregate';
      const reviewsOverview = await this.fetchReviewsOverview(reviewsOverviewUrl);
      this.renderReviewsOverview(reviewsOverview, true);
      this.dataLoaded = true;
    });
  }

  async fetchReviewsOverview(url) {
    try {
      const response = await fetch(url);

      if (!response.ok) {
        console.error(`Failed to fetch reviews. HTTP status: ${response.status}`);
        return null;
      }

      const data = await response.json();
      return data?.reviewAggregate || null;
    } catch (error) {
      console.error(`Error fetching reviews:`, error);
      return null;
    }
  }

  async renderReviewsOverview(reviewsOverview, animate = false) {
    if (!reviewsOverview) {
      return;
    }

    this.renderOverviewSummary(reviewsOverview);
    this.renderOverviewBreakdown(reviewsOverview);

    if (animate) {
      this.reviewsWidgetLoader.setAttribute('hidden', '');
      this.style.opacity = 1;
      this.style.transform = 'translateY(0)';
    }
  }

  renderOverviewBreakdown(reviewsOverview) {
    if (!reviewsOverview.reviewCountByLevel) return;

    const $breakdown = this.querySelectorAll(`[${attributes.breakdownItem}]`);
    $breakdown.forEach(($item) => {
      const rating = $item.getAttribute('data-rating');
      const reviewCount = reviewsOverview.reviewCountByLevel[`level${rating}Count`] || 0;
      const $barInner = $item.querySelector('.reviews-overview__bar-inner');
      const $count = $item.querySelector('.reviews-overview__breakdown-count');

      $barInner.style.setProperty('--width', `${(reviewCount / reviewsOverview.reviewCount) * 100}%`);
      $count.textContent = reviewCount.toLocaleString();
    });
  }

  renderOverviewSummary(reviewsOverview) {
    const reviewCount = reviewsOverview.reviewCount;
    if (!reviewCount) return;

    const averageRating = reviewsOverview.reviewRatingValuesTotal / reviewCount;
    const starsToDisplay = Math.round(averageRating);

    const $starsContainer = this.querySelector(`[${attributes.overviewStars}]`);
    const $averageRatingContainer = this.querySelector(`[${attributes.overviewCount}]`);

    $starsContainer.setAttribute('aria-label', `Average rating: ${averageRating.toFixed(1)} out of 5`);
    $starsContainer.innerHTML = generateStarRating(starsToDisplay);
    $averageRatingContainer.textContent = averageRating.toFixed(1);

    const reviewBasedOn = this.querySelector(`[${attributes.reviewBasedOn}]`);
    reviewBasedOn.textContent = `Based on ${reviewCount.toLocaleString()} reviews`;
  }
}

defineElement('reviews-overview', ReviewsOverview);
